/**
 * BoardPage — the gated shared status board (route `/board`, INSIDE ProtectedRoute).
 *
 * Story: epic-uat-portal-001-11.
 *
 * A logged-in tester sees a board of ALL reported bugs as summary rows. Clicking
 * a row opens a detail modal with the full body + screenshots. No tester ever
 * sees another tester's contact — the board surfaces only the member-safe fields
 * the service projects.
 *
 * Follows the house loading / error / empty / rows structure used by the other
 * feature pages (mirrors ReportPage's useState/try-catch shape): on mount it
 * lists the bugs, rendering a loading placeholder, an error banner (on a thrown
 * BugBoardApiError), an empty state (no bugs yet), or the rows. A 401 has already
 * cleared the session + redirected via authenticatedFetch; listBugs still throws
 * so we never show an empty board for a dead session.
 */

import { useEffect, useState } from 'react';
import {
  listBugs,
  BugBoardApiError,
  type MemberSafeBug,
} from '@/services/bugBoardService';
import { BugList } from './BugList';
import { BugDetailModal } from './BugDetailModal';

export function BoardPage() {
  const [bugs, setBugs] = useState<MemberSafeBug[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listBugs()
      .then((list) => {
        if (cancelled) return;
        setBugs(list);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof BugBoardApiError
            ? err.message
            : 'Could not load the status board. Please try again.',
        );
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main className="mx-auto w-full max-w-3xl px-6 py-12">
      <h1 className="mb-1 text-2xl font-bold" data-testid="board-heading">
        Reported bugs
      </h1>
      <p className="mb-6 text-sm text-muted-foreground">
        Every bug testers have reported. Open one to see the full report and
        screenshots. Reporters&apos; contact details are never shown here.
      </p>

      {loading && (
        <p data-testid="board-loading" className="py-8 text-center text-sm text-muted-foreground">
          Loading the board…
        </p>
      )}

      {error && (
        <div
          role="alert"
          data-testid="board-error"
          className="rounded border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {error}
        </div>
      )}

      {!loading && !error && bugs && bugs.length === 0 && (
        <div
          data-testid="board-empty"
          className="rounded border border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-muted-foreground"
        >
          No bugs have been reported yet. When testers file reports, they&apos;ll
          show up here.
        </div>
      )}

      {!loading && !error && bugs && bugs.length > 0 && (
        <BugList bugs={bugs} onSelect={setSelectedId} />
      )}

      {selectedId && (
        <BugDetailModal id={selectedId} onClose={() => setSelectedId(null)} />
      )}
    </main>
  );
}
